import {
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { Box, Divider, Grid, IconButton, Stack } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityLog as ActivityLogType } from '../../models/ActivityLog';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import { Palette } from 'Palette';
import { StyledCard, StyledCardContent, StyledHeading, StyledTypography } from '../styles/styles';
import { formatActivityLogTimestamp } from './activityLogEntryUtils';
import ContentDiffViewer, { ContentDiffPayload } from './ContentDiffViewer';
import ContentVersionModelInfoCard from './ContentVersionModelInfoCard';
import ContentVersionReadOnly from './contentVersionReadOnlyRender';

interface ContentVersionComparisonViewProps {
  tone: keyof typeof Palette;
  activityLog: ActivityLogType | null;
  diff: ContentDiffPayload | null;
  previousContent: ContentManagementInterface | null;
  currentContent: ContentManagementInterface | null;
}

const ContentVersionComparisonView: React.FC<ContentVersionComparisonViewProps> = ({
  tone,
  activityLog,
  diff,
  previousContent,
  currentContent,
}) => {
  const navigate = useNavigate();

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <HistoryIcon color="primary" />
        <Box>
          <StyledHeading variant="h4" tone={tone}>
            Content Version Comparison
          </StyledHeading>
          {activityLog && (
            <StyledTypography color="text.secondary" variant="body2">
              Activity: {activityLog.eventType} •{' '}
              {formatActivityLogTimestamp(activityLog.eventTimestamp)}
            </StyledTypography>
          )}
        </Box>
      </Stack>

      {activityLog && <ContentVersionModelInfoCard activityLog={activityLog} tone={tone} />}

      {diff && (
        <StyledCard tone={tone} sx={{ mb: 3 }}>
          <StyledCardContent>
            <ContentDiffViewer diff={diff} tone={tone} />
          </StyledCardContent>
        </StyledCard>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <StyledCard tone={tone}>
            <StyledCardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <HistoryIcon fontSize="small" />
                <StyledTypography variant="h6" tone={tone}>
                  Previous Version
                </StyledTypography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              {previousContent ? (
                <ContentVersionReadOnly content={previousContent} tone={tone} />
              ) : (
                <StyledTypography color="text.secondary">
                  Could not reconstruct previous version
                </StyledTypography>
              )}
            </StyledCardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledCard tone={tone}>
            <StyledCardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <VisibilityIcon fontSize="small" />
                <StyledTypography variant="h6" tone={tone}>
                  Current Version
                </StyledTypography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              {currentContent ? (
                <ContentVersionReadOnly content={currentContent} tone={tone} />
              ) : (
                <StyledTypography color="text.secondary">
                  Could not load current content
                </StyledTypography>
              )}
            </StyledCardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </>
  );
};

export default ContentVersionComparisonView;
